import * as React from 'react';
import { useElementWidth, useFluidGrid } from '@deltasierra/react-hooks';
import styled from 'styled-components';

export type DSFluidGridProps<T> = {
    getDimensions: (item: T) => Record<'height' | 'width', number>;
    itemMinWidth: number;
    items: T[];
    render: (
        props: React.HTMLAttributes<any> & { key: any },
        item: T,
        dimensions: Record<'height' | 'width', number>,
        index: number,
    ) => React.ReactNode;
    rowMaxHeight: number;
    rowMinHeight: number;
    /**
     * @default 10
     */
    spacing?: number;
};

const StyledRoot = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing(2)}px 0px;
`;

export const DSFluidGrid = <T, >(props: DSFluidGridProps<T>): React.ReactElement<DSFluidGridProps<T>> => {
    const { ref, width } = useElementWidth<HTMLDivElement>();
    const spacing = props.spacing ?? 10;
    const itemSizes = useFluidGrid({
        itemMinWidth: props.itemMinWidth,
        items: props.items.map(item => props.getDimensions(item)),
        rowMaxHeight: props.rowMaxHeight,
        rowMinHeight: props.rowMinHeight,
        rowWidth: width,
        spacing,
    });

    return (
        <StyledRoot ref={ref}>
            {itemSizes.map((dimensions, index) =>
                props.render(
                    {
                        key: index,
                        style: {
                            height: dimensions.height,
                            marginBottom: spacing,
                            width: dimensions.width,
                        },
                    },
                    props.items[index],
                    dimensions,
                    index,
                ),
            )}
        </StyledRoot>
    );
};
DSFluidGrid.displayName = 'DSFluidGrid';
